import {
  FC,
  useCallback,
  useState,
  MouseEvent,
  Fragment,
  useMemo,
} from "react";
import {
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  PopoverOrigin,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useFlag } from "@unleash/proxy-client-react";
import { useAppSelector } from "@/store/hooks";
import { CommitmentOrganizationSelectMenuItem } from "@/components/common/commitment-org-select/CommitmentOrganizationSelectMenuItem";
import {
  CommitmentCloudProvider,
  ProviderOrganisation,
  ProviderType,
} from "@/services/cloudchipr.api";
import { commitmentsOrganizationsSelector } from "@/store/commitments/selectors/organisations/commitmentsOrganizationsSelector";
import { getProviderName } from "@/utils/helpers/providers/getProviderName";

type CommitmentOrganization = {
  provider: ProviderType;
  organizationId?: string;
};

interface CommitmentOrganizationSelectProps {
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  selectedOrg: CommitmentOrganization;
  onChange(provider: CommitmentCloudProvider, orgId?: string): void;
  CustomTriggerComponent?: FC<{ onClick: (event: any) => void }>;
}

export const CommitmentOrganizationSelect: FC<
  CommitmentOrganizationSelectProps
> = ({
  onChange,
  anchorOrigin,
  selectedOrg,
  transformOrigin,
  CustomTriggerComponent,
}) => {
  const enableAzureCommitmentsForAlertsAndWidgets = useFlag(
    "EnableAzureCommitmentsForAlertsAndWidgets",
  );
  const selectedValue = getValue(
    selectedOrg.provider,
    selectedOrg.organizationId,
  );

  const commitmentOrganizations = useAppSelector((state) =>
    commitmentsOrganizationsSelector(
      state,
      enableAzureCommitmentsForAlertsAndWidgets,
    ),
  );

  const options = useMemo(
    () =>
      commitmentOrganizations?.map((org) => ({
        ...org,
        value: getValue(org.cloud_provider, org.organization_id),
      })),
    [commitmentOrganizations],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = useCallback(
    (value: string) => {
      const option = options?.find((org) => org.value === value);

      if (!option?.has_savings_plan) {
        return;
      }

      if (value !== selectedValue) {
        onChange(
          option?.cloud_provider as CommitmentCloudProvider,
          option?.organization_id ?? undefined,
        );
      }
      handleClose();
    },
    [options, onChange, selectedValue],
  );

  const getClickHandler = useCallback(
    (orgId: string) => {
      return () => handleSelect(orgId);
    },
    [handleSelect],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;

      handleSelect(value);
    },
    [handleSelect],
  );

  const renderValue = useCallback(
    (value: string) => {
      const selectedOption = options?.find((option) => option.value === value);

      if (!selectedOption) {
        return value;
      }

      return (
        <CommitmentOrganizationSelectMenuItem
          title={getTitle(selectedOption)}
          provider={selectedOption.cloud_provider}
        />
      );
    },
    [options],
  );

  if (
    !commitmentOrganizations?.length ||
    commitmentOrganizations?.length === 1
  ) {
    return null;
  }

  if (CustomTriggerComponent) {
    return (
      <Fragment>
        <CustomTriggerComponent onClick={handleOpen} />

        <Menu
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          {options?.map((org) => {
            const disabled = !org.has_savings_plan;
            const clickHandler = getClickHandler(org.value ?? "");

            return (
              <MenuItem
                key={org.value}
                value={org.value}
                disableRipple={disabled}
                onClick={clickHandler}
                sx={getMenuStyles(disabled)}
              >
                <CommitmentOrganizationSelectMenuItem
                  disabled={disabled}
                  title={getTitle(org)}
                  provider={org.cloud_provider}
                />
              </MenuItem>
            );
          })}
        </Menu>
      </Fragment>
    );
  }

  return (
    <FormControl fullWidth size="small" disabled={!options}>
      <InputLabel>Select Organization</InputLabel>

      <Select
        fullWidth
        size="small"
        label="Select Organization"
        onChange={handleChange}
        value={selectedValue}
        renderValue={renderValue}
      >
        {options?.map((org) => {
          const disabled = !org.has_savings_plan;

          return (
            <MenuItem
              key={org.value}
              value={org.value}
              disableRipple={disabled}
              sx={getMenuStyles(disabled)}
            >
              <CommitmentOrganizationSelectMenuItem
                disabled={disabled}
                title={getTitle(org)}
                provider={org.cloud_provider}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const getMenuStyles = (disabled?: boolean) => ({
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "default" : "pointer",
  "&:hover": disabled ? { bgcolor: "white" } : undefined,
});

const getValue = (provider: string, orgId?: string | null) => {
  if (provider === "aws" && !orgId) {
    return "";
  }

  if (!provider) {
    return orgId ?? "";
  }

  return `${provider}-${orgId ?? ""}`;
};

const getTitle = (org: ProviderOrganisation) =>
  org.name ??
  org.organization_id ??
  getProviderName(org.cloud_provider, { title: true }) ??
  undefined;
